import { useEffect, useState } from 'react';
import { performanceMonitor } from '../services/performanceMonitor';
import { recoveryService } from '../services/recoveryService';

interface PerformanceAlert {
  id: string;
  type: 'memory' | 'navigation' | 'api' | 'prediction';
  severity: 'low' | 'medium' | 'high';
  message: string;
  timestamp: number;
  autoResolve?: boolean;
}

export const usePerformanceAlerts = () => {
  const [alerts, setAlerts] = useState<PerformanceAlert[]>([]);
  const [isMonitoring, setIsMonitoring] = useState(true);

  useEffect(() => {
    if (!isMonitoring) return;

    const handlePerformanceAlert = (event: CustomEvent) => {
      const { metric, baseline, severity } = event.detail;
      
      const alert: PerformanceAlert = {
        id: `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: metric.type,
        severity,
        message: generateAlertMessage(metric, baseline),
        timestamp: Date.now(),
        autoResolve: severity === 'medium'
      };

      setAlerts(prev => [...prev, alert]);

      // Auto-resolve medium severity alerts after 30 seconds
      if (alert.autoResolve) {
        setTimeout(() => {
          setAlerts(prev => prev.filter(a => a.id !== alert.id));
        }, 30000);
      }

      // Notify super admin for high severity alerts
      if (severity === 'high') {
        recoveryService.notifySuperAdmin(
          { message: alert.message, type: 'performance_alert' },
          { metric, baseline, page: window.location.pathname }
        );
      }
    };

    // System health monitoring
    const healthCheckInterval = setInterval(() => {
      const health = performanceMonitor.getSystemHealth();
      
      // Check for critical health score
      if (health.score < 50) {
        const alert: PerformanceAlert = {
          id: `health_${Date.now()}`,
          type: 'prediction',
          severity: 'high',
          message: `Sistem sağlığı kritik seviyede: ${health.score}/100`,
          timestamp: Date.now()
        };
        
        setAlerts(prev => {
          // Avoid duplicate health alerts
          const hasRecentHealthAlert = prev.some(a => 
            a.type === 'prediction' && 
            Date.now() - a.timestamp < 60000
          );
          
          return hasRecentHealthAlert ? prev : [...prev, alert];
        });
      }

      // Check for predictions
      if (health.predictions.nextIssue > 0 && health.predictions.confidence > 0.6) {
        const alert: PerformanceAlert = {
          id: `prediction_${Date.now()}`,
          type: 'prediction',
          severity: health.predictions.nextIssue < 5 ? 'high' : 'medium',
          message: `${Math.round(health.predictions.nextIssue)} dakika içinde ${health.predictions.type} sorunu bekleniyor`,
          timestamp: Date.now(),
          autoResolve: true
        };
        
        setAlerts(prev => [...prev, alert]);
      }
    }, 30000); // Check every 30 seconds

    window.addEventListener('performance-alert', handlePerformanceAlert as EventListener);

    return () => {
      window.removeEventListener('performance-alert', handlePerformanceAlert as EventListener);
      clearInterval(healthCheckInterval);
    };
  }, [isMonitoring]);

  const dismissAlert = (alertId: string) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  };

  const dismissAllAlerts = () => {
    setAlerts([]);
  };

  const toggleMonitoring = () => {
    setIsMonitoring(prev => !prev);
    if (!isMonitoring) {
      setAlerts([]); // Clear alerts when stopping monitoring
    }
  };

  return {
    alerts,
    isMonitoring,
    dismissAlert,
    dismissAllAlerts,
    toggleMonitoring
  };
};

function generateAlertMessage(metric: any, baseline: number): string {
  const type = metric.type;
  const value = Math.round(metric.value);
  const baselineRounded = Math.round(baseline);
  
  switch (type) {
    case 'navigation':
      return `Sayfa yükleme süresi yavaş: ${value}ms (normal: ${baselineRounded}ms)`;
    case 'memory':
      return `Yüksek bellek kullanımı: ${value}MB (normal: ${baselineRounded}MB)`;
    case 'api':
      return `API yanıt süresi yavaş: ${value}ms (normal: ${baselineRounded}ms)`;
    default:
      return `Performans sorunu tespit edildi: ${type}`;
  }
}
